import { useDndMonitor } from '@dnd-kit/core'
import React, { useCallback, useMemo, useState } from 'react'

import {
    Bookable,
    OverlappingBookables,
    getBookableCardSize,
    getBookableCardStyle,
} from '../../models/Bookable'
import { Payee } from '../../models/Payee'
import { PIXELS_PER_MINUTE } from '../../utils/calendar'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import { CalendarBookableCard } from './CalendarBookableCard'
import { CalendarDraggable } from './CalendarDraggable'

type Props = {
    day: moment.Moment
    payees: Payee[]
    bookables?: Bookable[]
    bookablesGroup?: OverlappingBookables
    threshold?: number
    disableDrag?: boolean
}

export const CalendarDayDraggableBookables = ({
    day,
    payees,
    bookables,
    bookablesGroup,
    threshold = 2,
    disableDrag = false,
}: Props) => {
    const bookablesOrGroup = useMemo(
        () => bookables || bookablesGroup?.group || [],
        [bookables, bookablesGroup]
    )
    const bookablesToRender = useMemo(
        () => bookablesOrGroup.slice(0, threshold),
        [bookablesOrGroup, threshold]
    )
    const overflowBookables = useMemo(
        () => bookablesOrGroup.slice(threshold),
        [bookablesOrGroup, threshold]
    )

    const bookablesStyle: React.CSSProperties = useMemo(
        () =>
            !!bookablesGroup
                ? {
                      top: bookablesGroup.start * PIXELS_PER_MINUTE,
                      height: (bookablesGroup.end - bookablesGroup.start) * PIXELS_PER_MINUTE - 1,
                  }
                : {},
        [bookablesGroup]
    )

    const getBookableStyle = useCallback(
        (bookable: Bookable) =>
            !!bookablesGroup ? getBookableCardStyle(day, bookable, bookablesGroup.start) : {},
        [bookablesGroup, day]
    )

    const [showOverflowModal, setShowOverflowModal] = useState(false)
    const onClose = () => setShowOverflowModal(false)

    useDndMonitor({
        onDragStart: () => setShowOverflowModal(false),
    })

    return (
        <div
            className={`flex flex-1 py-px ${!!bookablesGroup ? 'absolute w-[calc(100%-8px)] flex-row gap-1' : 'flex-col gap-[2px]'}`}
            style={bookablesStyle}
        >
            {bookablesToRender
                .filter((b) => !!b.id)
                .map((bookable) => (
                    <CalendarDraggable
                        key={bookable.id}
                        id={bookable.id!}
                        bookable={bookable}
                        style={{ ...getBookableStyle(bookable), minWidth: 0 }}
                        disabled={disableDrag}
                    >
                        <CalendarBookableCard
                            bookable={bookable}
                            day={day}
                            payees={payees}
                            size={!!bookablesGroup ? getBookableCardSize(day, bookable) : 'sm'}
                            style={{ ...getBookableStyle(bookable), marginTop: 0 }}
                            showStatus={!!bookablesGroup}
                        />
                    </CalendarDraggable>
                ))}
            {overflowBookables.length > 0 && (
                <Button
                    className={`no-underline ${!!bookablesGroup ? 'flex h-full max-w-[31px] min-w-[31px] items-center justify-center bg-gray-100 p-0 hover:bg-gray-100 focus:bg-gray-100 active:bg-gray-100' : 'mt-auto h-6 self-start hover:bg-transparent focus:bg-transparent active:bg-transparent'}`}
                    intent="tertiary_deprecated"
                    onClick={() => setShowOverflowModal(true)}
                >
                    <Label size="xs" className="pointer-events-none text-gray-700">
                        +{overflowBookables.length} {!bookablesGroup ? 'more' : ''}
                    </Label>
                </Button>
            )}
            <Modal title="Additional appointments" isOpen={showOverflowModal} onClose={onClose}>
                <div className="flex flex-col gap-3">
                    {overflowBookables
                        .filter((b) => !!b.id)
                        .map((bookable) => (
                            <CalendarDraggable
                                key={bookable.id}
                                id={bookable.id!}
                                bookable={bookable}
                                disabled={disableDrag}
                            >
                                <CalendarBookableCard
                                    bookable={bookable}
                                    day={day}
                                    payees={payees}
                                    size="lg"
                                />
                            </CalendarDraggable>
                        ))}
                </div>
            </Modal>
        </div>
    )
}
